import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import CmsGallery from '../components/CmsGallery';
import { COMPANY_INFO, SERVICES_DATA, SEO_CONFIG } from '../data/companyData';
import { getOptimizedImageUrl } from '../lib/imageOptimizer';

export default function HomePage() {
  const heroImg = 'https://assets.auxodev.dk/assets/9badcd2d-12ea-450b-8280-42a177052604/hero-climate-refrigeration.jpg';
  const greenDevImg = 'https://assets.auxodev.dk/assets/9badcd2d-12ea-450b-8280-42a177052604/energioptimering-system.jpg';

  const whyChoosePoints = [
    {
      num: '01',
      tag: 'Tryghed & overblik',
      highlight: 'Før, under & efter installation',
      icon: 'fa-solid fa-compass-drafting',
      title: 'Professionel rådgivning',
      text: 'Vi yder grundig teknisk rådgivning før, under og efter installationen, så løsningen passer præcist til virksomhedens driftsbehov.'
    },
    {
      num: '02',
      tag: 'Maksimal oppetid',
      highlight: 'Gennemtestede kvalitetskomponenter',
      icon: 'fa-solid fa-shield-halved',
      title: 'Driftssikre løsninger',
      text: 'Vi arbejder udelukkende med gennemtestede anlæg og komponenter af høj kvalitet for at sikre minimal nedetid og maksimal oppetid.'
    },
    {
      num: '03',
      tag: 'Optimeret elforbrug',
      highlight: 'Markant lavere driftsudgifter',
      icon: 'fa-solid fa-gauge-high',
      title: 'Høj energieffektivitet',
      text: 'Vores anlæg dimensioneres og fintunes med henblik på lavest muligt elforbrug og optimal udnyttelse af tilført energi.'
    },
    {
      num: '04',
      tag: '100% Skræddersyet',
      highlight: 'Butik, erhverv & industribyggeri',
      icon: 'fa-solid fa-sliders',
      title: 'Tilpasset kundens behov',
      text: 'Ingen opgaver er ens. Vi skræddersyr køle-, fryse- og klimaløsninger til den enkelte butik, virksomhed eller industribygning.'
    },
    {
      num: '05',
      tag: 'Fremtidssikret teknik',
      highlight: 'Naturlige kølemidler & smart styring',
      icon: 'fa-solid fa-microchip',
      title: 'Klimateknisk ekspertise',
      text: 'Specialiseret viden inden for moderne køleteknik, varmepumper, naturlige kølemidler og intelligent anlægsstyring.'
    },
    {
      num: '06',
      tag: 'Stærk totaløkonomi',
      highlight: 'Færre uforudsete omkostninger',
      icon: 'fa-solid fa-chart-line-up',
      title: 'Langsigtet økonomi',
      text: 'En energieffektiv og driftssikker installation sikrer lavere samlede driftsomkostninger og reducerer uforudsete udgifter.'
    }
  ];

  return (
    <>
      <SEOHead
        title={SEO_CONFIG.home.title}
        description={SEO_CONFIG.home.description}
        image={heroImg}
      />

      {/* Hero Section */}
      <section className="relative bg-neutral-950 text-white overflow-hidden border-b border-neutral-800">
        {/* Subtle Background Glow Accent in Gold */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#C5A059]/10 rounded-full blur-3xl pointer-events-none z-10" />

        {/* Full-Height Edge-to-Edge Desktop Image on Right Side */}
        <div className="hidden lg:block absolute inset-y-0 right-0 w-3/5 xl:w-[62%] 2xl:w-[65%] z-0">
          <div className="relative w-full h-full">
            <img
              src={getOptimizedImageUrl(heroImg, { width: 2000, quality: 85 })}
              alt="Morine Køl & Klimateknik - Moderne køle- og klimaanlæg"
              className="w-full h-full object-cover object-center"
              loading="eager"
            />
            {/* Smooth Edge Gradients for text overlap readability */}
            <div className="absolute inset-y-0 left-0 w-2/5 bg-gradient-to-r from-neutral-950 via-neutral-950/75 to-transparent pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/85 via-neutral-950/20 to-neutral-950/40 pointer-events-none" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center min-h-[480px] lg:min-h-[560px]">
            {/* Copy overlapping photo */}
            <div className="lg:col-span-10 xl:col-span-10 2xl:col-span-10 space-y-6 max-w-3xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl relative z-10">
              <div>
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-neutral-900 border border-[#8D6D2F]/50 text-[#DFBF77] text-xs font-semibold tracking-wider uppercase shadow-xs">
                  <i className="fa-solid fa-snowflake-droplets text-[#C5A059]" aria-hidden="true" />
                  <span>Professionel Køle- & Klimateknik</span>
                </div>

                <h1 className="mt-4 pt-1 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl 2xl:text-[4rem] font-extrabold tracking-tight text-white leading-[1.15] max-w-5xl xl:max-w-6xl">
                  Professionelle løsninger inden for <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#DFBF77] via-[#C5A059] to-[#EAD5A5]">køl, klima og energioptimering</span>
                </h1>
              </div>

              <p className="text-base sm:text-lg text-neutral-300 max-w-2xl leading-relaxed">
                Morine Køl & Klimateknik hjælper virksomheder, butikker og industri med effektive, driftssikre og fremtidssikrede løsninger, der reducerer energiforbruget og sikrer optimal drift.
              </p>

              {/* Action Buttons */}
              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-lg bg-[#C5A059] hover:bg-[#DFBF77] text-neutral-950 font-bold text-base transition-all shadow-lg hover:shadow-[#C5A059]/20"
                >
                  <span>Få et tilbud</span>
                  <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
                </Link>

                <Link
                  to="/ydelser"
                  className="inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-lg bg-neutral-900/80 hover:bg-neutral-800 text-white font-semibold text-base transition-all border border-neutral-700 hover:border-neutral-500"
                >
                  <span>Se vores løsninger</span>
                  <i className="fa-solid fa-chevron-right text-xs text-[#DFBF77]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Mobile / Tablet Image (under lg) */}
            <div className="block lg:hidden relative w-full rounded-2xl overflow-hidden shadow-2xl border border-neutral-800 bg-neutral-900 h-[380px] sm:h-[480px]">
              <img
                src={getOptimizedImageUrl(heroImg, { width: 1200, quality: 85 })}
                alt="Morine Køl & Klimateknik - Moderne køle- og klimaanlæg"
                className="w-full h-full object-cover object-center"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/85 via-neutral-950/20 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar Section */}
      <section className="relative z-20 bg-neutral-950 border-b border-neutral-800/80 py-6 sm:py-8">
        {/* Subtle background glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-neutral-900/60 via-neutral-950 to-neutral-950 pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Item 1: Driftssikre anlæg */}
            <div className="group relative flex items-center gap-4 p-4 sm:p-5 rounded-2xl bg-neutral-900/70 hover:bg-neutral-900 border border-neutral-800/90 hover:border-emerald-500/40 transition-all duration-300 shadow-lg shadow-black/20 hover:shadow-emerald-950/20 backdrop-blur-sm">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/5 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0 group-hover:scale-105 transition-transform duration-300 shadow-inner">
                <i className="fa-solid fa-shield-check text-xl" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-white tracking-wide group-hover:text-emerald-300 transition-colors">
                  Driftssikre anlæg
                </div>
                <div className="text-xs text-neutral-400 mt-0.5">
                  Høj oppetid, sikkerhed & kvalitet
                </div>
              </div>
            </div>

            {/* Item 2: Reduceret energiforbrug */}
            <div className="group relative flex items-center gap-4 p-4 sm:p-5 rounded-2xl bg-neutral-900/70 hover:bg-neutral-900 border border-neutral-800/90 hover:border-[#C5A059]/40 transition-all duration-300 shadow-lg shadow-black/20 hover:shadow-[#C5A059]/10 backdrop-blur-sm">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C5A059]/25 to-[#9A7B38]/10 border border-[#C5A059]/40 flex items-center justify-center text-[#DFBF77] shrink-0 group-hover:scale-105 transition-transform duration-300 shadow-inner">
                <i className="fa-solid fa-bolt-lightning text-xl" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-white tracking-wide group-hover:text-[#DFBF77] transition-colors">
                  Reduceret energiforbrug
                </div>
                <div className="text-xs text-neutral-400 mt-0.5">
                  Maksimal effektivitet & besparelse
                </div>
              </div>
            </div>

            {/* Item 3: Erhverv & industri */}
            <div className="sm:col-span-2 lg:col-span-1 group relative flex items-center gap-4 p-4 sm:p-5 rounded-2xl bg-neutral-900/70 hover:bg-neutral-900 border border-neutral-800/90 hover:border-sky-500/40 transition-all duration-300 shadow-lg shadow-black/20 hover:shadow-sky-950/20 backdrop-blur-sm">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500/20 to-sky-600/5 border border-sky-500/30 flex items-center justify-center text-sky-400 shrink-0 group-hover:scale-105 transition-transform duration-300 shadow-inner">
                <i className="fa-solid fa-building text-xl" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-white tracking-wide group-hover:text-sky-300 transition-colors">
                  Erhverv & industri
                </div>
                <div className="text-xs text-neutral-400 mt-0.5">
                  Skræddersyet til jeres behov
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Kompetenceoversigt (Services Grid) */}
      <section className="py-20 lg:py-24 bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-xs font-bold text-[#8D6D2F] uppercase tracking-wider bg-[#FAF6ED] px-3.5 py-1.5 rounded-full border border-[#EAD5A5] shadow-xs">
              Arbejdsområder
            </span>
            <h2 className="mt-3.5 pt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-neutral-950">
              Vores centrale kompetencer
            </h2>
            <p className="mt-4 text-base text-neutral-600 leading-relaxed">
              Vi leverer skræddersyede køle- og klimaløsninger tilpasset ethvert teknisk behov – fra detailhandlens kølemøbler til komplekse industrianlæg.
            </p>
          </div>

          {/* Grid of 6-7 competencies */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {SERVICES_DATA.map((service, index) => {
              const highlights = [
                'Lavere elforbrug',
                'Høj effektivitet',
                'Naturlige kølemidler',
                'Detail & butikker',
                'Høj procesoppetid',
                'Støjsvag komfort',
                'Kompakte moduler'
              ];
              const highlight = highlights[index] || 'Kvalitetsløsning';
              const numStr = String(index + 1).padStart(2, '0');

              return (
                <Link
                  key={service.slug}
                  to={`/ydelser/${service.slug}`}
                  className="group relative flex flex-col justify-between p-6 rounded-2xl bg-neutral-50/80 hover:bg-white border border-neutral-200/90 hover:border-[#C5A059] transition-all duration-300 shadow-xs hover:shadow-xl hover:-translate-y-1 cursor-pointer overflow-hidden"
                >
                  {/* Subtle top decorative accent bar on hover */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C5A059] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  <div>
                    {/* Header: Icon & Number Badge */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-white border border-neutral-200 text-[#8D6D2F] flex items-center justify-center text-lg shadow-xs group-hover:bg-[#FAF6ED] group-hover:border-[#C5A059] group-hover:scale-105 transition-all duration-200">
                        <i className={service.icon} aria-hidden="true" />
                      </div>
                      <span className="text-xs font-black text-neutral-300 tracking-wider group-hover:text-[#C5A059] transition-colors">
                        {numStr}
                      </span>
                    </div>

                    <h3 className="text-lg font-bold tracking-tight text-neutral-900 group-hover:text-[#8D6D2F] transition-colors leading-snug">
                      {service.title}
                    </h3>

                    <p className="mt-2 text-xs sm:text-sm text-neutral-600 leading-relaxed line-clamp-3">
                      {service.description}
                    </p>
                  </div>

                  <div className="mt-5 pt-4 border-t border-neutral-200/70">
                    <div className="mb-2.5">
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-neutral-700 bg-neutral-200/60 group-hover:bg-[#FAF6ED] group-hover:text-[#8D6D2F] px-2.5 py-0.5 rounded-md transition-colors">
                        <i className="fa-solid fa-check text-[10px] text-emerald-600" aria-hidden="true" />
                        {highlight}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs font-bold text-[#8D6D2F]">
                      <span>Læs mere</span>
                      <i className="fa-solid fa-arrow-right text-[11px] group-hover:translate-x-1.5 transition-transform duration-200" aria-hidden="true" />
                    </div>
                  </div>
                </Link>
              );
            })}

            {/* 8th Card: Custom Solution & Contact (completes 2 rows of 4 perfectly) */}
            <Link
              to="/kontakt"
              className="group relative flex flex-col justify-between p-6 rounded-2xl bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 text-white border border-neutral-800 hover:border-[#C5A059] transition-all duration-300 shadow-md hover:shadow-2xl hover:-translate-y-1 cursor-pointer overflow-hidden"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#C5A059] via-[#DFBF77] to-[#8D6D2F]" />

              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-neutral-900 border border-[#C5A059]/40 text-[#DFBF77] flex items-center justify-center text-lg shadow-inner group-hover:bg-[#8D6D2F] group-hover:text-neutral-950 group-hover:scale-105 transition-all duration-200">
                    <i className="fa-solid fa-headset" aria-hidden="true" />
                  </div>
                  <span className="text-xs font-black text-[#C5A059]/80 tracking-wider">
                    08
                  </span>
                </div>

                <div className="inline-block mb-1">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#DFBF77] bg-[#C5A059]/20 px-2 py-0.5 rounded border border-[#C5A059]/30">
                    Specialløsning
                  </span>
                </div>

                <h3 className="text-lg font-bold tracking-tight text-white group-hover:text-[#DFBF77] transition-colors leading-snug">
                  Skræddersyet rådgivning
                </h3>

                <p className="mt-2 text-xs sm:text-sm text-neutral-300 leading-relaxed">
                  Har du et specifikt projekt eller brug for uforpligtende teknisk rådgivning? Vores teknikere står klar til at hjælpe.
                </p>
              </div>

              <div className="mt-5 pt-4 border-t border-neutral-800 flex items-center justify-between text-xs font-bold text-[#DFBF77]">
                <span>Kontakt os i dag</span>
                <i className="fa-solid fa-arrow-right text-[11px] group-hover:translate-x-1.5 transition-transform duration-200" aria-hidden="true" />
              </div>
            </Link>
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-2 text-sm font-bold text-neutral-900 hover:text-[#8D6D2F] transition-colors"
            >
              <span>Se detaljeret oversigt over alle ydelser</span>
              <i className="fa-solid fa-arrow-right text-xs text-[#C5A059]" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Grøn Udvikling / Energioptimering Focus Section */}
      <section className="relative bg-neutral-950 text-white overflow-hidden border-b border-neutral-800">
        {/* Subtle background glow */}
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-96 h-96 bg-[#C5A059]/10 rounded-full blur-3xl pointer-events-none z-10" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none z-10" />

        {/* Full-Height Edge-to-Edge Desktop Image on Right Side */}
        <div className="hidden lg:block absolute inset-y-0 right-0 w-1/2 xl:w-[50%] 2xl:w-[48%] z-0">
          <div className="relative w-full h-full">
            <img
              src={getOptimizedImageUrl(greenDevImg, { width: 1600, quality: 85 })}
              alt="Energioptimering af moderne køleanlæg"
              className="w-full h-full object-cover object-center"
              loading="lazy"
            />
            {/* Smooth Edge Gradients for seamless blend & readability */}
            <div className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-neutral-950 via-neutral-950/70 to-transparent pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/80 via-transparent to-neutral-950/30 pointer-events-none" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Left text column */}
            <div className="lg:col-span-6 xl:col-span-6 space-y-6">
              <div>
                <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#DFBF77] uppercase tracking-wider px-3.5 py-1.5 rounded-full bg-neutral-900 border border-neutral-800 shadow-xs">
                  <i className="fa-solid fa-leaf text-emerald-400 text-xs" aria-hidden="true" />
                  Fremtidssikring & Energioptimering
                </span>

                <h2 className="mt-4 pt-1 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
                  Målrettet fokus på <span className="text-[#DFBF77]">reduceret energiforbrug</span> og grøn udvikling
                </h2>
              </div>

              <p className="text-neutral-300 text-base sm:text-lg leading-relaxed">
                Køle- og klimaanlæg står ofte for en væsentlig del af virksomheders samlede elforbrug. Med Morine Køl & Klimateknik som samarbejdspartner sikres moderne løsninger, der forener høj driftssikkerhed med markante energibesparelser og lavere driftsomkostninger.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 pb-2">
                {[
                  { title: 'Lavere energiforbrug', desc: 'Mærkbare reduktioner i faste udgifter' },
                  { title: 'Moderne installationer', desc: 'Fremtidssikrede og effektive anlæg' },
                  { title: 'Bedre driftsøkonomi', desc: 'Mindre slitage og længere levetid' },
                  { title: 'Grønne løsninger', desc: 'Miljøvenlige kølemidler' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-emerald-950 border border-emerald-500/80 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="fa-solid fa-check text-emerald-400 text-[10px]" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-white">{item.title}</h4>
                      <p className="text-[11px] text-neutral-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Link
                  to="/ydelser/energioptimering"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#C5A059] hover:bg-[#DFBF77] text-neutral-950 font-bold text-sm transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                >
                  <span>Læs om energioptimering</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <Link
                  to="/ydelser/groen-udvikling"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white font-semibold text-sm transition-all border border-neutral-800 hover:-translate-y-0.5"
                >
                  <span>Grøn udvikling</span>
                </Link>
              </div>
            </div>

            {/* Mobile / Tablet Image (under lg) */}
            <div className="block lg:hidden relative w-full rounded-2xl overflow-hidden shadow-2xl border border-neutral-800 bg-neutral-900 h-[360px] sm:h-[440px]">
              <img
                src={getOptimizedImageUrl(greenDevImg, { width: 1200, height: 900, quality: 85 })}
                alt="Energioptimering af moderne køleanlæg"
                className="w-full h-full object-cover object-center"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/90 via-neutral-950/20 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Hvorfor vælge Morine Køl & Klimateknik */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-neutral-100/70 via-neutral-50 to-white border-b border-neutral-200/90 relative overflow-hidden">
        {/* Subtle decorative background ambient dots */}
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:24px_24px] opacity-40 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
            <span className="inline-flex items-center gap-2 text-xs font-bold text-[#8D6D2F] uppercase tracking-wider bg-[#FAF6ED] px-3.5 py-1.5 rounded-full border border-[#EAD5A5] shadow-xs">
              <i className="fa-solid fa-award text-[#8D6D2F]" aria-hidden="true" />
              Faglig tilgang & tryghed
            </span>
            <h2 className="mt-3.5 pt-1 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-neutral-950">
              Hvorfor vælge <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8D6D2F] via-[#A88238] to-[#8D6D2F]">Morine Køl & Klimateknik</span>
            </h2>
            <p className="mt-4 text-base sm:text-lg text-neutral-600 leading-relaxed">
              Vi forener dyb teknisk ekspertise med personlig rådgivning og kompromisløst fokus på stabil drift i hverdagen.
            </p>
          </div>

          {/* Grid of 6 unique distinctive cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {whyChoosePoints.map((item, idx) => (
              <div
                key={idx}
                className="group relative p-7 sm:p-8 rounded-3xl bg-white border border-neutral-200/90 shadow-sm hover:shadow-xl hover:border-[#DFBF77]/70 transition-all duration-300 flex flex-col justify-between overflow-hidden hover:-translate-y-1.5"
              >
                {/* Subtle top accent line on hover */}
                <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-[#DFBF77] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <div>
                  {/* Header row with Icon, Number and Tag */}
                  <div className="flex items-start justify-between gap-4 mb-6">
                    <div className="relative">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FAF6ED] to-[#F3EAD5] border border-[#EAD5A5]/80 text-[#8D6D2F] flex items-center justify-center text-xl shadow-xs group-hover:scale-105 group-hover:bg-[#FAF6ED] group-hover:text-[#8D6D2F] group-hover:border-[#DFBF77] group-hover:shadow-md transition-all duration-300">
                        <i className={item.icon} aria-hidden="true" />
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <span className="font-mono text-2xl font-black tracking-tight text-neutral-200 group-hover:text-[#DFBF77]/80 transition-colors duration-300 select-none">
                        {item.num}
                      </span>
                      <span className="inline-flex items-center text-[11px] font-semibold text-neutral-600 bg-neutral-100 group-hover:bg-[#FAF6ED] group-hover:text-[#8D6D2F] group-hover:border-[#EAD5A5]/70 px-2.5 py-0.5 rounded-full border border-neutral-200/70 transition-colors">
                        {item.tag}
                      </span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold tracking-tight text-neutral-950 group-hover:text-[#8D6D2F] transition-colors duration-200">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                    {item.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dynamic CMS Gallery Section */}
      <section className="py-20 lg:py-24 bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CmsGallery
            slug="forside"
            title="Udvalgte projekter og installationer"
            description="Et visuelt indblik i vores klimatekniske anlæg, varmepumper, butikskøl og industrielle installationer."
          />
        </div>
      </section>
    </>
  );
}
